import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchDnsecho } from '../api/client'
import type { DnsechoRow } from '@shared/api'

/**
 * Delays between polls, in ms. Widening gaps: resolvers that only handle part of
 * the traffic tend to show up late, so the tail is worth waiting through.
 * Total is about 31.5s across ten attempts, matching the previous behaviour.
 */
export const POLL_DELAYS = [0, 1000, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000]
export const MAX_POLLS = POLL_DELAYS.length

/** Hex of the user's IPv4 plus 45 random chars, as the old client produced. */
export function makeIdentifier(ip: string): string {
    const int = ip.split('.').reduce((acc, oct) => (acc << 8) + parseInt(oct, 10), 0) >>> 0
    const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let random = ''
    const bytes = new Uint8Array(45)
    crypto.getRandomValues(bytes)
    for (const b of bytes) random += alphabet[b % alphabet.length]
    return `${int.toString(16)}-${random}`
}

export interface LeakTestState {
    rows: DnsechoRow[]
    /** Which poll each resolver was first observed at; drives the timeline chart. */
    firstSeen: Map<string, number>
    poll: number
    running: boolean
    elapsedMs: number
    stop: () => void
}

export function useLeakTest(identifier: string | undefined): LeakTestState {
    const [poll, setPoll] = useState(0)
    const [stopped, setStopped] = useState(false)
    const [elapsedMs, setElapsed] = useState(0)
    const firstSeen = useRef(new Map<string, number>())
    const startedAt = useRef<number>(0)

    // a new identifier is a new test: discard everything from the previous run
    useEffect(() => {
        firstSeen.current = new Map()
        startedAt.current = Date.now()
        setPoll(0)
        setStopped(false)
        setElapsed(0)
    }, [identifier])

    const running = Boolean(identifier) && !stopped && poll < MAX_POLLS

    const { data } = useQuery({
        queryKey: ['dnsecho', identifier, poll],
        queryFn: ({ signal }) => fetchDnsecho(identifier as string, signal),
        enabled: running,
        // results accumulate rather than flashing empty between polls
        placeholderData: (prev) => prev,
        gcTime: 0,
        retry: false,
    })

    // schedule the next poll once the current one has landed
    useEffect(() => {
        if (!running || data === undefined) return
        const timer = setTimeout(() => {
            setPoll((p) => p + 1)
            setElapsed(Date.now() - startedAt.current)
        }, POLL_DELAYS[Math.min(poll + 1, MAX_POLLS - 1)])
        return () => clearTimeout(timer)
    }, [running, data, poll])

    // record the first sighting of each resolver
    useEffect(() => {
        if (!data) return
        for (const row of data) {
            if (!firstSeen.current.has(row.ip)) firstSeen.current.set(row.ip, poll)
        }
    }, [data, poll])

    const stop = useCallback(() => setStopped(true), [])

    return {
        rows: data ?? [],
        firstSeen: firstSeen.current,
        poll,
        running,
        elapsedMs,
        stop,
    }
}
